import { Link, useNavigate } from "react-router-dom";
import { Rating, BenefitList, BuyButton, ProductCard } from "@/components/ProductCard";
import { AdSlot, useAdSettings } from "@/components/AdSlot";
import { ComparisonTable } from "@/components/ComparisonTable";

const TOKEN_RE = /\[(product|compare):([a-fA-F0-9,\s]+)\]/g;

export function InlineProductCard({ product }) {
  return (
    <aside
      data-testid={`inline-product-${product.id}`}
      className="not-prose my-8 grid sm:grid-cols-[180px_1fr] gap-5 rounded-2xl border border-[#E5E0D8] bg-white p-4 shadow-[0_8px_24px_rgba(44,43,41,0.06)]"
    >
      <Link to={`/product/${product.id}`} className="block aspect-square rounded-xl overflow-hidden bg-[#EADDC8]/30">
        <img src={product.image_url} alt={product.title} loading="lazy" className="w-full h-full object-cover" />
      </Link>
      <div className="flex flex-col">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#D17A58] font-semibold">Our pick</span>
        <Link to={`/product/${product.id}`} className="font-display text-xl leading-snug text-[#2C2B29] hover:text-[#5B7B53] mt-1">
          {product.title}
        </Link>
        <div className="flex items-center gap-2 mt-1.5">
          <Rating value={product.rating} />
          <span className="text-xs text-[#5C5A56]">{product.rating} · {product.review_count.toLocaleString()} reviews</span>
        </div>
        <BenefitList items={product.benefits} testid={`inline-benefits-${product.id}`} />
        <div className="mt-4 flex flex-wrap items-center gap-4">
          <span className="text-lg font-semibold text-[#2C2B29]">{product.price}</span>
          <BuyButton product={product} testid={`inline-buy-${product.id}`} />
        </div>
      </div>
    </aside>
  );
}

const boldify = (t) => t
  .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
  .replace(/\[([^\]]+)\]\((\/[^)\s]*)\)/g, '<a href="$2" class="text-[#D17A58] font-medium underline underline-offset-4 decoration-[#D17A58]/40 hover:decoration-[#D17A58]">$1</a>')
  .replace(/\n/g, "<br/>");

export function BlogContent({ content, products }) {
  const adSettings = useAdSettings();
  const navigate = useNavigate();
  const onLinkClick = (e) => {
    const a = e.target.closest("a[href^='/']");
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    navigate(a.getAttribute("href"));
    window.scrollTo(0, 0);
  };
  const interval = adSettings?.ad_interval || 3;
  const parts = content.split(TOKEN_RE);
  const blocks = [];
  let textCount = 0;
  parts.forEach((part, i) => {
    if (i % 3 === 1) return;
    if (i % 3 === 2) {
      if (parts[i - 1] === "product") {
        if (products[part]) blocks.push({ type: "product", id: part, key: `p-${i}-${part}` });
      } else {
        const items = part.split(",").map((x) => products[x.trim()]).filter(Boolean);
        if (items.length >= 2) blocks.push({ type: "compare", items, key: `c-${i}` });
      }
      return;
    }
    part.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean)
      .forEach((para, j) => {
        blocks.push({ type: "text", html: boldify(para), key: `t-${i}-${j}` });
        textCount += 1;
        if (textCount % interval === 0) blocks.push({ type: "ad", key: `ad-${textCount}` });
      });
  });
  while (blocks.length && blocks[blocks.length - 1].type === "ad") blocks.pop();
  return (
    <div data-testid="blog-content" onClick={onLinkClick} className="max-w-none text-lg text-[#2C2B29] leading-relaxed">
      {blocks.map((b) => {
        if (b.type === "product") return <InlineProductCard key={b.key} product={products[b.id]} />;
        if (b.type === "compare") return <ComparisonTable key={b.key} products={b.items} title="Compare our picks" testid={`blog-compare-${b.key}`} />;
        if (b.type === "ad") return <AdSlot key={b.key} type="in-article" testid={`blog-ad-${b.key}`} />;
        return <p key={b.key} className="mb-5" dangerouslySetInnerHTML={{ __html: b.html }} />;
      })}
    </div>
  );
}

export function EditorsPicks({ post }) {
  const ids = post.related_product_ids?.length ? post.related_product_ids : Object.keys(post.products || {});
  const items = ids.map((id) => post.products?.[id]).filter(Boolean);
  if (!items.length) return null;
  return (
    <section data-testid="editors-picks" className="bg-[#EADDC8]/30 border-t border-[#E5E0D8]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <p className="text-xs uppercase tracking-[0.2em] text-[#5B7B53] mb-2">Shop this article</p>
        <h2 className="font-display text-4xl text-[#2C2B29] mb-10">Editor's picks.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </div>
    </section>
  );
}
